import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import ToastMessage from "../../utils/mui/righttoptoast";
import {
  decideCorrection,
  decideCorrectionsBulk,
  errorMessage,
  fetchCorrectionQueue,
} from "./correctionsApi";
import {
  DECISION_LABEL,
  STATUS_LABEL,
  clockLabel,
  clockOutProblem,
  clockOutValue,
  dateTimeLabel,
  dayLabel,
  hhmm,
  hoursDelta,
  isPlaceholderOut,
} from "./correctionTime";

/* Approver queue for automatic clock-out corrections (owner 10-19: Dr. Gowtham approves everyone's,
   his own included). The server decides who is an approver (403 for anyone else) and re-checks every
   bound. Approve / Reject answer an employee's time; Set time / Keep recorded work on any open
   request, so employees who do not use CARE (or who did not answer in 3 days) are covered too. */

const VIEWS = [
  { key: "decide", label: "Needs decision", statuses: ["pending_approval"] },
  { key: "noresponse", label: "No response", statuses: ["no_response"] },
  { key: "waiting", label: "Waiting for employee", statuses: ["awaiting_employee", "rejected"] },
  { key: "decided", label: "Decided", statuses: ["approved", "rejected_final"] },
];
const viewOf = (key) => VIEWS.find((v) => v.key === key) || VIEWS[0];

const CorrectionsQueue = ({ onCountsChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState("");
  const [view, setView] = useState("decide");
  const [selected, setSelected] = useState([]);
  const [setting, setSetting] = useState({}); // id -> {time, error}
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // item
  const [rejectNote, setRejectNote] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "info", message: "" });

  const showToast = (message, severity = "info") => setToast({ open: true, severity, message });
  // the parent's badge refresh; a ref so a new callback each render never reloads the queue
  const countsChangedRef = useRef(onCountsChanged);
  countsChangedRef.current = onCountsChanged;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCorrectionQueue("all");
      setItems((res?.data?.items || []).filter((it) => it.status !== "cancelled"));
      setForbidden("");
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(errorMessage(err, "Only the attendance approver can see this."));
      else showToast(errorMessage(err, "Could not load the corrections."), "error");
      setItems([]);
    } finally {
      setLoading(false);
      setSelected([]);
      if (countsChangedRef.current) countsChangedRef.current();
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const out = {};
    VIEWS.forEach((v) => {
      out[v.key] = items.filter((it) => v.statuses.includes(it.status)).length;
    });
    return out;
  }, [items]);

  const shown = useMemo(() => items.filter((it) => viewOf(view).statuses.includes(it.status)), [items, view]);

  const decide = async (item, action, extra = {}) => {
    setBusyId(item.id);
    try {
      await decideCorrection(item.id, { action, ...extra });
      showToast(`${item.emp_name || `#${item.emp_id}`} ${dayLabel(item.work_date)}: ${DECISION_LABEL[action] || "saved"}.`, "success");
      setSetting((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      await load();
    } catch (err) {
      showToast(errorMessage(err, "Not saved. Try again."), "error");
      if (err?.response?.status === 409) await load();
    } finally {
      setBusyId(null);
    }
  };

  const saveSetTime = (item) => {
    const s = setting[item.id] || {};
    const problem = clockOutProblem(item, s.time);
    if (problem) {
      setSetting((prev) => ({ ...prev, [item.id]: { ...s, error: problem } }));
      return;
    }
    decide(item, "set", { clockOutTime: clockOutValue(item, s.time) });
  };

  const approveSelected = async () => {
    if (!selected.length) return;
    setLoading(true);
    try {
      const res = await decideCorrectionsBulk(selected, "approve");
      const failed = res?.data?.failed || 0;
      showToast(failed ? `${res?.data?.done || 0} approved, ${failed} not saved (reload and check).`
        : `${res?.data?.done || 0} approved.`, failed ? "warning" : "success");
    } catch (err) {
      showToast(errorMessage(err, "Not saved. Try again."), "error");
    } finally {
      await load();
    }
  };

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectable = shown.filter((it) => it.status === "pending_approval").map((it) => it.id);

  if (forbidden) {
    return <div className="text-center py-8 text-gray-500 text-sm">{forbidden}</div>;
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap gap-2">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => {
                setView(v.key);
                setSelected([]);
              }}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                view === v.key ? "bg-[#1D2951] text-white border-[#1D2951]" : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              {v.label} ({counts[v.key] || 0})
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {view === "decide" && selectable.length > 0 && (
            <Button
              size="small"
              variant="contained"
              disabled={!selected.length || loading}
              onClick={approveSelected}
              sx={{ textTransform: "none", backgroundColor: "#1D2951" }}
            >
              Approve selected ({selected.length})
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={load} disabled={loading} sx={{ textTransform: "none" }}>
            Reload
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-left">
              <th className="p-2 w-8">
                {view === "decide" && selectable.length > 0 && (
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={selected.length === selectable.length}
                    onChange={() => setSelected(selected.length === selectable.length ? [] : selectable)}
                  />
                )}
              </th>
              <th className="p-2">Employee</th>
              <th className="p-2">Day</th>
              <th className="p-2">Clock in</th>
              <th className="p-2">Recorded out</th>
              <th className="p-2">Employee's time</th>
              <th className="p-2">Other sessions</th>
              <th className="p-2">Status</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && !shown.length ? (
              <tr>
                <td colSpan={9} className="text-center py-6">
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : !shown.length ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-500">Nothing here.</td>
              </tr>
            ) : (
              shown.map((item) => {
                const s = setting[item.id];
                const busy = busyId === item.id;
                const isOpen = ["awaiting_employee", "pending_approval", "rejected", "no_response"].includes(item.status);
                return (
                  <tr key={item.id} className="border-t border-gray-100 align-top">
                    <td className="p-2">
                      {item.status === "pending_approval" && (
                        <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
                      )}
                    </td>
                    <td className="p-2">
                      <div className="font-medium text-gray-900">{item.emp_name || "-"}</div>
                      <div className="text-xs text-gray-500">#{item.emp_id}</div>
                    </td>
                    <td className="p-2 whitespace-nowrap">{dayLabel(item.work_date)}</td>
                    <td className="p-2 whitespace-nowrap">{clockLabel(item.clock_in)}</td>
                    <td className="p-2 whitespace-nowrap">
                      {clockLabel(item.recorded_clock_out)}
                      <div className="text-xs text-red-600">automatic</div>
                    </td>
                    <td className="p-2">
                      {item.proposed_clock_out ? (
                        <>
                          <div className="whitespace-nowrap">
                            {clockLabel(item.proposed_clock_out)}{" "}
                            <span className="text-xs text-gray-500">
                              ({hoursDelta(item.recorded_clock_out, item.proposed_clock_out)} h)
                            </span>
                          </div>
                          {item.employee_note && <div className="text-xs text-gray-600 mt-0.5">"{item.employee_note}"</div>}
                          {item.submit_count > 1 && <div className="text-xs text-gray-500">resubmitted</div>}
                        </>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {(item.other_sessions || []).length
                        ? item.other_sessions.map((o) => (
                            <div key={o.timesheet_id} className="whitespace-nowrap">
                              {clockLabel(o.clock_in)} - {o.clock_out ? clockLabel(o.clock_out) : "open"}
                            </div>
                          ))
                        : "-"}
                    </td>
                    <td className="p-2 text-xs">
                      <div className="font-medium">{STATUS_LABEL[item.status] || item.status}</div>
                      {item.status === "awaiting_employee" && item.respond_by && (
                        <div className="text-gray-500">answer by {dateTimeLabel(item.respond_by)}</div>
                      )}
                      {item.status === "rejected" && item.respond_by && (
                        <div className="text-gray-500">may resubmit until {dateTimeLabel(item.respond_by)}</div>
                      )}
                      {!isOpen && item.final_clock_out && (
                        <div className="text-gray-600">
                          {DECISION_LABEL[item.decision] || item.decision}: {clockLabel(item.final_clock_out)}
                        </div>
                      )}
                      {item.decision_note && <div className="text-gray-500">"{item.decision_note}"</div>}
                    </td>
                    <td className="p-2">
                      {isOpen && (
                        <div className="flex flex-col gap-1 min-w-[150px]">
                          {item.status === "pending_approval" && (
                            <div className="flex gap-1">
                              <Button size="small" variant="contained" disabled={busy}
                                onClick={() => decide(item, "approve")}
                                sx={{ textTransform: "none", backgroundColor: "#15803d" }}>
                                Approve
                              </Button>
                              <Button size="small" variant="outlined" color="error" disabled={busy}
                                onClick={() => {
                                  setRejecting(item);
                                  setRejectNote("");
                                }}
                                sx={{ textTransform: "none" }}>
                                Reject
                              </Button>
                            </div>
                          )}
                          {s ? (
                            <div>
                              <div className="flex gap-1 items-center">
                                <input
                                  type="time"
                                  aria-label="Clock-out time"
                                  value={s.time || ""}
                                  onChange={(e) => setSetting((prev) => ({ ...prev, [item.id]: { time: e.target.value, error: "" } }))}
                                  className="border border-gray-300 rounded px-1 py-0.5 text-sm"
                                />
                                <Button size="small" disabled={busy} onClick={() => saveSetTime(item)} sx={{ textTransform: "none", minWidth: 0 }}>
                                  Save
                                </Button>
                                <Button size="small" disabled={busy}
                                  onClick={() => setSetting((prev) => {
                                    const next = { ...prev };
                                    delete next[item.id];
                                    return next;
                                  })}
                                  sx={{ textTransform: "none", minWidth: 0 }}>
                                  Cancel
                                </Button>
                              </div>
                              {s.error && <div className="text-xs text-red-600">{s.error}</div>}
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              <Button size="small" variant="text" disabled={busy}
                                onClick={() => setSetting((prev) => ({
                                  ...prev,
                                  [item.id]: {
                                    time: hhmm(item.proposed_clock_out || (isPlaceholderOut(item) ? "" : item.recorded_clock_out)),
                                    error: "",
                                  },
                                }))}
                                sx={{ textTransform: "none" }}>
                                Set time
                              </Button>
                              {/* 11:59 PM is the cron's end-of-day closure, not a clock-out (owner 10-19) */}
                              {!isPlaceholderOut(item) && (
                                <Button size="small" variant="text" disabled={busy}
                                  onClick={() => decide(item, "keep")}
                                  sx={{ textTransform: "none" }}>
                                  Keep {clockLabel(item.recorded_clock_out)}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!rejecting} onClose={() => setRejecting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject this clock-out?</DialogTitle>
        <DialogContent>
          {rejecting && (
            <p className="text-sm text-gray-700 mb-2">
              {rejecting.emp_name || `#${rejecting.emp_id}`}, {dayLabel(rejecting.work_date)}: {clockLabel(rejecting.proposed_clock_out)}.{" "}
              {(rejecting.submit_count || 0) < (rejecting.max_submissions || 2)
                ? "The employee can send one more time."
                : isPlaceholderOut(rejecting)
                  ? "This was the resubmission. The recorded 11:59 PM is the automatic end of day and cannot stand: use Set time instead."
                  : `This was the resubmission: the recorded ${clockLabel(rejecting.recorded_clock_out)} will stand (or use Set time instead).`}
            </p>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Reason (shown to the employee)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value.slice(0, 1000))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const item = rejecting;
              setRejecting(null);
              decide(item, "reject", { note: rejectNote });
            }}
            sx={{ textTransform: "none" }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <ToastMessage
        message={toast.message}
        severity={toast.severity}
        open={toast.open}
        handleClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default CorrectionsQueue;
